import React from 'react';

import { useParams } from 'react-router-dom';

import Box from '@amzn/meridian/box';
import Column from '@amzn/meridian/column';
import Link from '@amzn/meridian/link';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import { useQuery } from '@tanstack/react-query';
import moment from 'moment';
import EmDash from 'src/components/shared/emdash';
import LoadingSpinner from 'src/components/shared/loadingSpinner/loadingSpinner';
import { RTW_SYSTEM } from 'src/constants';
import { getAssociateProfileQueryOptions } from 'src/services/associateProfile/associateProfileQueries';
import { useAppSelector } from 'src/store/store';

import ExportActionMenu from '../placements/placementActions/exportActions';
import UpdateActionMenu from '../placements/placementActions/updateActions';
import PlacementInfo from '../placements/placementInfo';

const AssociateProfileHistory = () => {
  const { alias } = useParams();
  const { selectedSite } = useAppSelector((state) => state.sites);

  const historyQuery = useQuery(getAssociateProfileQueryOptions.history(alias ?? '', selectedSite.id));

  if (historyQuery.isPending) {
    return <LoadingSpinner />;
  }

  if (historyQuery.isError) {
    return <Text>Oops! Something went wrong.</Text>;
  }

  const history = historyQuery.data;

  return (
    <Column spacing="400">
      <Text type="b500">Closed Placements ({history.length})</Text>
      {history.map((placement) => (
        <Box
          key={placement.accommodationId}
          spacingInset="400 450"
          maxWidth="750"
          type="fill"
          backgroundColor="highlight"
        >
          <Column spacing="400">
            <Row widths={['fit', 'fill']}>
              <Column spacing="200">
                <Row>
                  <Text type="h100" tag="span">
                    Case ID:&nbsp;
                    <Link type="secondary" href={`/placementDetails/${placement.accommodationId}`}>
                      {placement.accommodationType} | {placement.externalCaseId ?? <EmDash />}
                    </Link>
                  </Text>
                </Row>
                <Row spacing="200">
                  <Text color="secondary">
                    Last Updated: {moment(placement.updatedAt).format('MM/DD/YYYY [at] h:mma')} UTC •
                  </Text>
                  {placement.updatedBy === RTW_SYSTEM ? (
                    <Text color="secondary">{placement.updatedBy}</Text>
                  ) : (
                    <Link
                      href={`https://phonetool.amazon.com/users/${placement.updatedBy}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {placement.updatedBy}@
                    </Link>
                  )}
                </Row>
              </Column>
              <Row spacing="300" alignmentHorizontal="end">
                <ExportActionMenu placement={placement} />
                <UpdateActionMenu placement={placement} />
              </Row>
            </Row>
            <Row spacing="600">
              <PlacementInfo label="Injury" value={placement.injury} />
              <PlacementInfo label="Start Date" value={new Date(placement.startAt).toLocaleDateString()} />
              <PlacementInfo label="End Date" value={new Date(placement.endAt).toLocaleDateString()} />
              <PlacementInfo label="Closed on" value={new Date(placement.closedAt).toLocaleDateString()} />
              <PlacementInfo label="Job(s)" value={placement.allocatedJobShifts.length} />
            </Row>
          </Column>
        </Box>
      ))}
    </Column>
  );
};

export default AssociateProfileHistory;
